import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Phone } from "lucide-react";
import { SITE } from "../../lib/site";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-inkline bg-warmwhite px-4 py-3 text-[1rem] text-charcoal placeholder:text-charcoal/40 transition-colors focus:border-forest focus:outline-none";
const labelCls = "block text-[0.85rem] font-medium text-forest mb-1.5";

const WHO = ["Parent", "Spouse", "Relative", "Friend", "Myself", "Other"];
const WHEN = ["Today / urgently", "Within a few days", "Within a few weeks", "Just exploring"];

export default function FamilyInquiryForm({ home = "" }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    whoNeedsCare: "Parent",
    situation: home ? `I'd like to ask about ${home}.` : "",
    timeframe: WHEN[3],
    preferredContact: "Phone",
    consent: false,
    website: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  async function submit(e) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!form.consent) {
      toast.error("Please acknowledge the privacy note so we can follow up.");
      return;
    }
    setStatus("submitting");
    try {
      if (!form.website) {
        const { consent, website, ...payload } = form;
        await axios.post(`${API}/inquiries/family`, payload);
      }
      trackEvent("family_inquiry_submitted");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please try again, or call Doris directly.");
    }
  }

  if (status === "success") {
    return (
      <div
        data-testid="family-inquiry-success"
        className="rounded-2xl border border-forest/20 bg-sage-mist p-8 text-center md:p-10"
        role="status"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-forest" aria-hidden="true" />
        <h3 className="mt-5 font-serif text-2xl font-medium text-forest">
          Thank you. Your message has been sent to CareCraft.
        </h3>
        <p className="mx-auto mt-4 max-w-md leading-relaxed text-charcoal/80">
          If the situation is urgent, please call {SITE.phoneDisplay}.
        </p>
        <a
          href={SITE.phoneHref}
          data-testid="family-inquiry-success-call"
          className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-forest px-6 py-3 font-medium text-ivory transition-colors hover:bg-forest-dark"
        >
          <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
        </a>
        <p className="mt-6 text-[0.8rem] text-charcoal/55">{SITE.name} is not an emergency service.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="family-inquiry-form" noValidate>
      {home && (
        <p
          data-testid="family-inquiry-home"
          className="mb-5 inline-flex items-center gap-2 rounded-full border border-clay/40 bg-clay-soft px-4 py-2 text-[0.85rem] font-medium text-forest"
        >
          Asking about: {home}
        </p>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="f-name" className={labelCls}>
            Your name
          </label>
          <input
            id="f-name"
            type="text"
            required
            data-testid="family-inquiry-name"
            className={inputCls}
            value={form.name}
            onChange={set("name")}
            autoComplete="name"
            placeholder="Jane Garcia"
          />
        </div>
        <div>
          <label htmlFor="f-phone" className={labelCls}>
            Phone
          </label>
          <input
            id="f-phone"
            type="tel"
            required
            data-testid="family-inquiry-phone"
            className={inputCls}
            value={form.phone}
            onChange={set("phone")}
            autoComplete="tel"
            placeholder="(818) 555-0123"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="f-email" className={labelCls}>
            Email
          </label>
          <input
            id="f-email"
            type="email"
            required
            data-testid="family-inquiry-email"
            className={inputCls}
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
            placeholder="you@example.com"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="f-who" className={labelCls}>
            Who needs care?
          </label>
          <select
            id="f-who"
            data-testid="family-inquiry-who"
            className={inputCls}
            value={form.whoNeedsCare}
            onChange={set("whoNeedsCare")}
          >
            {WHO.map((w) => (
              <option key={w}>{w}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="f-situation" className={labelCls}>
            What is happening?
          </label>
          <textarea
            id="f-situation"
            required
            rows={4}
            data-testid="family-inquiry-situation"
            className={inputCls}
            value={form.situation}
            onChange={set("situation")}
            placeholder="Tell us in your own words. There's no wrong way to start."
          />
        </div>
        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>How soon do you need help?</legend>
          <div className="flex flex-wrap gap-2.5">
            {WHEN.map((w) => (
              <label
                key={w}
                className={`cursor-pointer rounded-full border px-4 py-2.5 text-[0.9rem] transition-colors ${
                  form.timeframe === w
                    ? "border-forest bg-forest text-ivory"
                    : "border-inkline bg-warmwhite text-charcoal/75 hover:border-forest/40"
                }`}
              >
                <input
                  type="radio"
                  name="timeframe"
                  value={w}
                  checked={form.timeframe === w}
                  onChange={set("timeframe")}
                  className="sr-only"
                />
                {w}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>Preferred way to reach you</legend>
          <div className="flex flex-wrap gap-2.5">
            {["Phone", "Email"].map((m) => (
              <label
                key={m}
                className={`cursor-pointer rounded-full border px-4 py-2.5 text-[0.9rem] transition-colors ${
                  form.preferredContact === m
                    ? "border-forest bg-forest text-ivory"
                    : "border-inkline bg-warmwhite text-charcoal/75 hover:border-forest/40"
                }`}
              >
                <input
                  type="radio"
                  name="preferredContact"
                  value={m}
                  checked={form.preferredContact === m}
                  onChange={set("preferredContact")}
                  className="sr-only"
                />
                {m}
              </label>
            ))}
          </div>
        </fieldset>
        {/* Honeypot — hidden from real users */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="f-website">Website</label>
          <input id="f-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
        </div>
        <div className="sm:col-span-2">
          <label className="flex max-w-xl cursor-pointer items-start gap-3 text-[0.85rem] leading-relaxed text-charcoal/70">
            <input
              type="checkbox"
              required
              data-testid="family-inquiry-consent"
              checked={form.consent}
              onChange={set("consent")}
              className="mt-1 h-4 w-4 shrink-0 accent-[#214A40]"
            />
            <span>
              I agree that CareCraft may contact me about my inquiry. I understand CareCraft is not an
              emergency service. See our{" "}
              <Link to="/privacy" className="stitch-underline text-forest">
                Privacy Policy
              </Link>
              .
            </span>
          </label>
        </div>
        <div className="sm:col-span-2">
          <button
            type="submit"
            data-testid="family-inquiry-submit-button"
            disabled={status === "submitting"}
            className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-3.5 font-medium text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-dark disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Sending…" : "Send to CareCraft"}
          </button>
          {status === "error" && (
            <p role="alert" className="mt-4 text-[0.9rem] text-clay">
              Your message couldn't be sent just now. Please try again, or call {SITE.phoneDisplay}.
            </p>
          )}
        </div>
      </div>
    </form>
  );
}
